import { useMemo } from 'react';
import { SchoolOutput } from '@/api/gql/graphql';
import { Checkbox } from '@/components/Common';
import MenuDropdown from '@/components/Common/DropdownMenu/MenuDropdown';
import { getEstablishmentTypeLabel, getStatusLabel } from '../utils/helpers';
import {
  EstablishmentMenuItem,
  EstablishmentTableColumn,
} from '../utils/types';

// Types pour les données formatées
export interface FormattedEstablishmentData {
  _id: string;
  name: JSX.Element;
  type: string;
  email: string;
  status: JSX.Element;
  Action: JSX.Element;
  [key: string]: unknown; // Index signature pour compatibilité DataTable
}

// Hook pour formater les données des établissements
export const useEstablishmentData = (
  establishments: SchoolOutput[] | undefined,
  selectedEstablishments: string[],
  handleEstablishmentSelection: (id: string) => void,
  createActionsMenu: (id: string) => EstablishmentMenuItem[],
) => {
  // Colonnes de la table
  const columns: EstablishmentTableColumn[] = [
    { field: 'name', header: 'Nom' },
    { field: 'type', header: 'Type' },
    { field: 'email', header: 'Email' },
    { field: 'status', header: 'Statut' },
    { field: 'Action', header: 'Action' },
  ];

  // Données formatées pour la table
  const formattedData: FormattedEstablishmentData[] = useMemo(() => {
    if (!establishments?.length) return [];

    return establishments.map((establishment) => ({
      _id: establishment._id,
      name: (
        <div className="flex items-center">
          <Checkbox
            onClick={() => handleEstablishmentSelection(establishment._id)}
            checked={selectedEstablishments.includes(establishment._id)}
            value={establishment._id}
          />
          <span className="ml-4">
            {establishment.name}
            <span className="block text-[#616163]">
              {getEstablishmentTypeLabel(establishment.type || '')}
            </span>
          </span>
        </div>
      ),
      type: getEstablishmentTypeLabel(establishment.type || ''),
      email: establishment.contact?.email || 'N/A',
      status: (
        <span
          className={`px-2 py-1 rounded-full text-xs ${
            establishment.status === 'ACTIVE' ? 'text-running' : 'text-primary'
          }`}
        >
          {getStatusLabel(establishment.status || '')}
        </span>
      ),
      Action: (
        <div className="w-[70%] flex justify-end items-center">
          <div className="flex items-center gap-2">
            <MenuDropdown
              menuItems={createActionsMenu(establishment._id)}
              dotsStyle="w-[30px] h-[30px]"
            />
          </div>
        </div>
      ),
    }));
  }, [
    establishments,
    selectedEstablishments,
    handleEstablishmentSelection,
    createActionsMenu,
  ]);

  return {
    columns,
    formattedData,
  };
};
